// In-memory mock catalog. Used until NetSuite is wired up.
// Remove this file (and the fallback in controllers/catalog.js) once the
// real catalog handler returns data.
//
// Shape mirrors what the NetSuite catalog handler will normalize. Matrix
// items show up here as a parent with `colors` (matrix dim 1) and `sizes`
// (matrix dim 2). Per-color price adjustments and per-color galleries are
// represented inline; the variant SKU is computed when needed.

export const MOCK_ITEMS = [
  {
    id: "rats-pack",
    sku: "RATS",
    name: "RATS Pack Mock",
    shortDescription:
      "Rapid-access medic bag with one-handed entry to organized supplies. Built for the front line — and the few seconds that matter.",
    tagline: "A medic bag for the front line.",
    description:
      "Every detail of the Rapid Access Trauma System points to one goal: get to what you need, fast. Trauma shears, tourniquet holders, four removable pockets with clear vinyl windows and two IV bag pockets are laid out for one-handed access. Mounted on the NICE Frame it leaves room for litters or combi tools below; the removable BVS keeps the fit dialed in with or without body armor.",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    fittingArticleSlug: "fitting-and-sizing",
    price: { amount: 825, currency: "USD" },
    prices: [{ amount: 825, currency: "USD" }],
    priceLevels: { base: 825, msrp: 875, wholesale: 660, dealer: 700 },
    image: "/rats-black.avif",
    gallery: ["/rats-black.avif", "/rats-coyote.avif", "/rats-multicam.avif"],
    colors: [
      { id: "black", name: "Black", hex: "#1a1a1a", image: "/rats-black.avif",
        gallery: ["/rats-black.avif"], priceAdjustment: 0 },
      { id: "coyote", name: "Coyote", hex: "#a78a6b", image: "/rats-coyote.avif",
        gallery: ["/rats-coyote.avif"], priceAdjustment: 0 },
      { id: "multicam", name: "MultiCam", image: "/rats-multicam.avif",
        gallery: ["/rats-multicam.avif"], priceAdjustment: 50 },
    ],
    sizes: ["S", "M", "L"],
    volume: 25,
    productLine: "military",
    intendedUse: ["Assault Pack", "Medic"],
    frameSystem: "NICE Frame",
    isNew: false,
    rating: 4.5,
    reviewCount: 36,
    specs: [
      { label: "Weight",          us: "5 lb 4 oz",         metric: "2.38 kg" },
      { label: "Volume",          us: "1525 cu-ins",       metric: "25 L",
            hint: "How is volume calculated?" },
      { label: "Dimensions",      us: '20"x12"x10"',       metric: "50.8x30.5x25.4 cm",
            hint: "How are dimensions calculated?" },
      { label: "Frame System",    value: "NICE Frame compatible" },
      { label: "Harnessing Style", value: "Modular hip belt" },
    ],
    origin: { label: "Made in Vietnam", note: "Lifetime warranty", flag: "🌏" },
    attributes: {
      defaultOpen: "documentation",
      sections: [
        { id: "keyAttributes", title: "Key Attributes",
          bullets: [
            "Frontline medic bag with one-handed access to organized supplies.",
            "Reinforced handles for hanging the pack on branches or in helicopters.",
            "Removable BVS optimizes use with or without body armor.",
          ] },
        { id: "features",      title: "Features",
          bullets: [
            "Multiple trauma shears + tourniquet holders.",
            "Four removable pockets with clear vinyl windows.",
            "Two IV bag pockets.",
            "Sized to leave room below for litters or hydraulic combi tools.",
          ] },
        { id: "materials",     title: "Materials",
          bullets: [
            "500D Cordura body fabric.",
            "1000D Cordura bottom fabric.",
            "YKK zippers throughout.",
          ] },
        { id: "documentation", title: "Documentation",
          links: [{ label: "RATS PACK - Spec Sheet", href: "#" }] },
        { id: "partNumbers",   title: "Part Numbers",
          parts: [
            { sku: "RATS-BLACK-S",    color: "Black",    size: "S" },
            { sku: "RATS-BLACK-M",    color: "Black",    size: "M" },
            { sku: "RATS-BLACK-L",    color: "Black",    size: "L" },
            { sku: "RATS-COYOTE-M",   color: "Coyote",   size: "M" },
            { sku: "RATS-MULTICAM-M", color: "MultiCam", size: "M" },
          ] },
      ],
    },
  },
  {
    id: "3-day-assault",
    sku: "3DAB",
    name: "3 Day Assault BVS Mock",
    shortDescription:
      "The pack that started it all — built for sustainment gear, ammo, IFAKs, and tactical radios. Versatile, mobile, and ready to move.",
    tagline: "The original 3 Day Assault.",
    description:
      "Our best-seller, sized for everything you need over a hard 72 hours. The 3-ZIP design opens fast for sustainment gear, ops kit, and radios. The Bolstered Ventilation & Stability (BVS) system locks the pack down over body armor without sacrificing airflow. Three rows of external MOLLE give you room to expand, and side compression straps cinch it back down when you don't need it.",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    fittingArticleSlug: "fitting-and-sizing",
    price: { amount: 595, currency: "USD" },
    prices: [{ amount: 595, currency: "USD" }],
    priceLevels: { base: 595, msrp: 625, wholesale: 476, dealer: 505 },
    image: "/rats-coyote.avif",
    gallery: ["/rats-coyote.avif", "/rats-multicam.avif"],
    colors: [
      { id: "coyote", name: "Coyote", hex: "#a78a6b", image: "/rats-coyote.avif",
        gallery: ["/rats-coyote.avif"], priceAdjustment: 0 },
      { id: "multicam", name: "MultiCam", image: "/rats-multicam.avif",
        gallery: ["/rats-multicam.avif"], priceAdjustment: 30 },
    ],
    sizes: ["S", "M", "L"],
    volume: 30,
    productLine: "military",
    intendedUse: ["Assault Pack"],
    frameSystem: "Guide Light Frame",
    isNew: false,
    rating: 4.8,
    reviewCount: 165,
    specs: [
      { label: "Weight",          us: "4.5 lb",            metric: "2.04 kg" },
      { label: "Volume",          us: "2185 cu-ins",       metric: "35.8 L",
            hint: "How is volume calculated?" },
      { label: "Dimensions",      us: '22.75"x14"x12.5"',  metric: "57.8x35.6x31.7 cm",
            hint: "How are dimensions calculated?" },
      { label: "Frame System",    value: "Futura Frame" },
      { label: "Harnessing Style", value: "Limited" },
    ],
    origin: { label: "Made in the USA", note: "Berry Compliant", flag: "🇺🇸" },
    attributes: {
      defaultOpen: "documentation",
      sections: [
        { id: "keyAttributes", title: "Key Attributes",
          bullets: [
            "BVS – Bolstered Ventilation and Stability system – allows for a stable, secure fit over body armor and can be easily removed.",
            "Featuring our classic 3-ZIP design for easy, rapid access to the interior.",
            "Internal Frame is an HDPE (high-density polyethylene) sheet that provides rigidity and yet is flexible. The Futura Yoke easily micro-adjusts to the torso length allowing the proper amount of stand-off between the back and frame.",
          ] },
        { id: "features",      title: "Features",
          bullets: [
            "3-ZIP main compartment access.",
            "Three rows of external MOLLE webbing on the front + sides.",
            "Side compression straps to minimize bulk.",
            "Sustainment-ready interior layout for ammo, IFAKs, radios.",
          ] },
        { id: "materials",     title: "Materials",
          bullets: [
            "500D Cordura body fabric.",
            "HDPE internal frame sheet.",
            "Mil-spec webbing and hardware.",
          ] },
        { id: "documentation", title: "Documentation",
          links: [{ label: "3 DAY ASSAULT BVS - Spec Sheet", href: "#" }] },
        { id: "partNumbers",   title: "Part Numbers",
          parts: [
            { sku: "3DAB-COYOTE-S",   color: "Coyote",   size: "S" },
            { sku: "3DAB-COYOTE-M",   color: "Coyote",   size: "M" },
            { sku: "3DAB-COYOTE-L",   color: "Coyote",   size: "L" },
            { sku: "3DAB-MULTICAM-M", color: "MultiCam", size: "M" },
            { sku: "3DAB-MULTICAM-L", color: "MultiCam", size: "L" },
          ] },
      ],
    },
  },
  {
    id: "blackjack-100",
    sku: "BJ100",
    name: "Blackjack 100 Mock",
    tagline: "Built for the long haul.",
    shortDescription:
      "Heavy-haul mission pack for sustainment loads and extended ops. Big main compartment, rugged frame, no compromises.",
    description:
      "When the mission stretches days and the load list keeps growing, Blackjack 100 carries it. A 100-liter cavern paired with a heavy-duty NICE Frame moves serious weight without flexing under it.",
    price: { amount: 1285, currency: "USD" },
    prices: [{ amount: 1285, currency: "USD" }],
    priceLevels: { base: 1285, msrp: 1350, wholesale: 1028 },
    image: "/rats-coyote.avif",
    gallery: ["/rats-coyote.avif"],
    colors: [
      { id: "coyote", name: "Coyote", hex: "#a78a6b", image: "/rats-coyote.avif",
        gallery: ["/rats-coyote.avif"], priceAdjustment: 0 },
      { id: "multicam", name: "MultiCam", image: "/rats-multicam.avif",
        gallery: ["/rats-multicam.avif"], priceAdjustment: 60 },
    ],
    sizes: ["M", "L"],
    volume: 100,
    productLine: "military",
    intendedUse: ["Assault and Sustainment"],
    frameSystem: "NICE Frame",
    isNew: false,
    rating: 4.6,
    reviewCount: 24,
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    fittingArticleSlug: "fitting-and-sizing",
    specs: [
      { label: "Weight",          us: "8 lb 6 oz",   metric: "3.80 kg" },
      { label: "Volume",          us: "6100 cu-ins", metric: "100 L",
            hint: "How is volume calculated?" },
      { label: "Load Range",      us: "60–120 lb",   metric: "27–54 kg" },
      { label: "Frame System",    value: "NICE Frame" },
      { label: "Harnessing Style", value: "Modular hip belt" },
    ],
    origin: { label: "Made in Vietnam", note: "Lifetime warranty", flag: "🌏" },
    attributes: {
      defaultOpen: "keyAttributes",
      sections: [
        { id: "keyAttributes", title: "Key Attributes",
          bullets: [
            "100 L heavy-haul main compartment for sustainment loads.",
            "NICE Frame with reinforced stays for extended carry under load.",
            "Compatible with NICE-mount accessories — Lid Pouch, MOLLE, hydration.",
          ] },
        { id: "features", title: "Features",
          bullets: [
            "Top-loading clamshell with internal organization.",
            "External MOLLE on front and sides for kit expansion.",
            "Removable hip belt for swap to body armor.",
          ] },
        { id: "materials", title: "Materials",
          bullets: ["500D Cordura body.", "1000D Cordura bottom.", "Mil-spec hardware."] },
        { id: "documentation", title: "Documentation",
          links: [{ label: "Blackjack 100 - Spec Sheet", href: "#" }] },
        { id: "partNumbers", title: "Part Numbers",
          parts: [
            { sku: "BJ100-COYOTE-M", color: "Coyote", size: "M" },
            { sku: "BJ100-COYOTE-L", color: "Coyote", size: "L" },
            { sku: "BJ100-MULTICAM-M", color: "MultiCam", size: "M" },
            { sku: "BJ100-MULTICAM-L", color: "MultiCam", size: "L" },
          ] },
      ],
    },
  },
  {
    id: "mlf",
    sku: "MLF",
    name: "Military Light Frame (MLF) Mock",
    shortDescription: "Lightweight load-bearing frame for tactical loadouts.",
    description: "Lightweight aluminum frame designed to carry mission-critical loads without adding bulk.",
    price: { amount: 550, currency: "USD" },
    prices: [{ amount: 550, currency: "USD" }],
    priceLevels: { base: 550 },
    image: "/rats-coyote.avif",
    gallery: ["/rats-coyote.avif"],
    colors: [
      { id: "coyote", name: "Coyote", hex: "#a78a6b", image: "/rats-coyote.avif",
        gallery: ["/rats-coyote.avif"], priceAdjustment: 0 },
    ],
    sizes: [],
    volume: 0,
    productLine: "military",
    intendedUse: ["Body Armor Accessory"],
    frameSystem: "Military Light Frame",
    isNew: true,
    rating: 0,
    reviewCount: 0,
    specs: [
      { group: "Capacity", label: "Weight", value: "1 lb 14 oz" },
      { group: "Materials", label: "Stays", value: "Aluminum 6061-T6" },
      { group: "Materials", label: "Frame Sheet", value: "HDPE" },
      { group: "Fit", label: "Torso Range", value: '15–21"' },
      { group: "Compatibility", label: "Pack Compatibility", value: "NICE-mount packs (RATS, Blackjack 100)" },
      { group: "Other", label: "Country of Origin", value: "United States" },
      { group: "Other", label: "Warranty", value: "Lifetime" },
    ],
  },
  {
    id: "fire-pulaski",
    sku: "FIREP",
    name: "Fire Pulaski Mock",
    tagline: "Built for the line.",
    shortDescription:
      "Hand-crew pack with quick-deploy lid, tool carry, and a frame tuned for long shifts on the line.",
    description:
      "Hard miles, harder days. The Fire Pulaski runs cool against your back and rides quiet over rough terrain. A stout frame, fire shelter pocket and dedicated tool stow keep your kit organized when seconds matter.",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    fittingArticleSlug: "fitting-and-sizing",
    price: { amount: 459, currency: "USD" },
    prices: [{ amount: 459, currency: "USD" }],
    priceLevels: { base: 459, msrp: 489, wholesale: 367 },
    image: "/rats-coyote.avif",
    gallery: ["/rats-coyote.avif"],
    colors: [
      { id: "yellow", name: "High-Vis Yellow", hex: "#e7c64a", image: "/rats-coyote.avif", priceAdjustment: 0 },
      { id: "black", name: "Black", hex: "#1a1a1a", image: "/rats-black.avif", priceAdjustment: 0 },
    ],
    sizes: ["M", "L"],
    volume: 45,
    productLine: "fire",
    intendedUse: ["Wildland Fire", "Hand Crew"],
    frameSystem: "Wildfire Frame",
    isNew: true,
    rating: 4.7,
    reviewCount: 41,
    specs: [
      { label: "Weight",      us: "5 lb 2 oz",  metric: "2.32 kg" },
      { label: "Volume",      us: "2750 cu-ins", metric: "45 L",
            hint: "How is volume calculated?" },
      { label: "Dimensions",  us: '24"x12"x10"', metric: "61x30.5x25.4 cm",
            hint: "How are dimensions calculated?" },
      { label: "Frame System",    value: "Wildfire Frame" },
      { label: "Harnessing Style", value: "Adjustable yoke" },
    ],
    origin: { label: "Made in the USA", note: "NFPA 1977 compliant", flag: "🇺🇸" },
    madeInUSA: true,
    attributes: {
      defaultOpen: "keyAttributes",
      sections: [
        { id: "keyAttributes", title: "Key Attributes",
          bullets: [
            "Quick-deploy lid clears in one motion for fire shelter access.",
            "Dedicated Pulaski / hand-tool carry with reinforced contact points.",
            "Fire-rated fabric meets NFPA 1977 standards.",
          ] },
        { id: "features", title: "Features",
          bullets: [
            "Hydration sleeve sized for 3 L reservoir.",
            "Hi-vis lid trim and reflective accents.",
            "Side compression for shorter sustainment loads.",
          ] },
        { id: "materials", title: "Materials",
          bullets: ["Fire-rated 1000D Cordura body.", "Aramid thread.", "Mil-spec hardware."] },
        { id: "documentation", title: "Documentation",
          links: [{ label: "Fire Pulaski - Spec Sheet", href: "#" }] },
        { id: "partNumbers", title: "Part Numbers",
          parts: [
            { sku: "FIREP-YELLOW-M", color: "High-Vis Yellow", size: "M" },
            { sku: "FIREP-YELLOW-L", color: "High-Vis Yellow", size: "L" },
            { sku: "FIREP-BLACK-M",  color: "Black",          size: "M" },
            { sku: "FIREP-BLACK-L",  color: "Black",          size: "L" },
          ] },
      ],
    },
  },
  {
    id: "metcalf-50",
    sku: "MET50",
    name: "Metcalf 50 Mock",
    tagline: "For elk camp and the long pack-out.",
    shortDescription:
      "A 50 L hunting pack tuned for deep backcountry trips. Quiet fabric, load shelf, balanced frame.",
    description:
      "Days in. Days out. The Metcalf 50 is shaped for the trip back when the load is heaviest. A quiet brushed face fabric runs silent through timber, the integrated load shelf swallows quartered game, and the Guide Light Frame transfers the weight where you want it — on your hips.",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    fittingArticleSlug: "fitting-and-sizing",
    price: { amount: 575, currency: "USD" },
    prices: [{ amount: 575, currency: "USD" }],
    priceLevels: { base: 575, msrp: 625, wholesale: 460 },
    image: "/rats-coyote.avif",
    gallery: ["/rats-coyote.avif"],
    colors: [
      { id: "olive", name: "Foliage", hex: "#5b6648", image: "/rats-coyote.avif", priceAdjustment: 0 },
      { id: "stone", name: "Stone Grey", hex: "#8a8a82", image: "/rats-coyote.avif", priceAdjustment: 0 },
    ],
    sizes: ["S", "M", "L"],
    volume: 50,
    productLine: "hunting",
    intendedUse: ["Backcountry Hunting"],
    frameSystem: "Guide Light Frame",
    isNew: false,
    rating: 4.9,
    reviewCount: 88,
    specs: [
      { label: "Weight",      us: "5 lb 8 oz",  metric: "2.49 kg" },
      { label: "Volume",      us: "3050 cu-ins", metric: "50 L",
            hint: "How is volume calculated?" },
      { label: "Dimensions",  us: '26"x13"x11"', metric: "66x33x28 cm",
            hint: "How are dimensions calculated?" },
      { label: "Load Range",  us: "30–80 lb",   metric: "13.6–36.3 kg" },
      { label: "Frame System",     value: "Guide Light Frame" },
      { label: "Harnessing Style", value: "Adjustable yoke + load shelf" },
    ],
    origin: { label: "Made in the USA", note: "Lifetime warranty", flag: "🇺🇸" },
    madeInUSA: true,
    attributes: {
      defaultOpen: "keyAttributes",
      sections: [
        { id: "keyAttributes", title: "Key Attributes",
          bullets: [
            "Quiet brushed face fabric for stalking through timber.",
            "Integrated load shelf for quartered meat or oversized loads.",
            "Guide Light Frame transfers weight to the hips, not the shoulders.",
          ] },
        { id: "features", title: "Features",
          bullets: [
            "Top-loading main with floating lid pocket.",
            "Bow / rifle scabbard compatible.",
            "Side compression and removable hip belt.",
          ] },
        { id: "materials", title: "Materials",
          bullets: ["330D Cordura body.", "500D Cordura bottom.", "Brushed face for sound dampening."] },
        { id: "documentation", title: "Documentation",
          links: [{ label: "Metcalf 50 - Spec Sheet", href: "#" }] },
        { id: "partNumbers", title: "Part Numbers",
          parts: [
            { sku: "MET50-OLIVE-S", color: "Foliage",    size: "S" },
            { sku: "MET50-OLIVE-M", color: "Foliage",    size: "M" },
            { sku: "MET50-OLIVE-L", color: "Foliage",    size: "L" },
            { sku: "MET50-STONE-M", color: "Stone Grey", size: "M" },
            { sku: "MET50-STONE-L", color: "Stone Grey", size: "L" },
          ] },
      ],
    },
  },
  {
    id: "edc-2-day",
    sku: "EDC2D",
    name: "EDC 2-Day Mock",
    tagline: "Daily driver. Weekend ready.",
    shortDescription:
      "A clean-line 25 L pack that handles the daily commute and the quick getaway. Padded laptop sleeve, soft-touch fabric, clamshell access.",
    description:
      "Designed to disappear in a meeting and carry the load on the road. A padded laptop sleeve, soft-touch face fabric, and a wide clamshell opening make EDC 2-Day the pack you grab without thinking — and don't put down for the weekend.",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    price: { amount: 249, currency: "USD" },
    prices: [{ amount: 249, currency: "USD" }],
    priceLevels: { base: 249, msrp: 269, wholesale: 199 },
    image: "/rats-black.avif",
    gallery: ["/rats-black.avif"],
    colors: [
      { id: "black", name: "Black", hex: "#1a1a1a", image: "/rats-black.avif", priceAdjustment: 0 },
      { id: "shadow", name: "Shadow", hex: "#3a3a3a", image: "/rats-black.avif", priceAdjustment: 0 },
      { id: "sand", name: "Sand", hex: "#cdb892", image: "/rats-coyote.avif", priceAdjustment: 0 },
    ],
    sizes: [],
    volume: 25,
    productLine: "everyday",
    intendedUse: ["Daily Carry", "Travel"],
    frameSystem: "Frameless",
    isNew: true,
    rating: 4.6,
    reviewCount: 313,
    specs: [
      { label: "Weight",      us: "2 lb 4 oz",  metric: "1.02 kg" },
      { label: "Volume",      us: "1525 cu-ins", metric: "25 L",
            hint: "How is volume calculated?" },
      { label: "Dimensions",  us: '19"x11"x7"',  metric: "48x28x18 cm" },
      { label: "Laptop Fit",  value: "Up to 16″" },
      { label: "Frame System", value: "Frameless" },
    ],
    origin: { label: "Made in Vietnam", note: "Lifetime warranty", flag: "🌏" },
    attributes: {
      defaultOpen: "keyAttributes",
      sections: [
        { id: "keyAttributes", title: "Key Attributes",
          bullets: [
            "Padded laptop sleeve fits up to a 16″ machine.",
            "Soft-touch face fabric reads professional in the office.",
            "Wide clamshell access — no digging.",
          ] },
        { id: "features", title: "Features",
          bullets: [
            "Quick-grab top pocket for keys and cards.",
            "Stretch water-bottle pocket on each side.",
            "Trolley pass-through for travel days.",
          ] },
        { id: "materials", title: "Materials",
          bullets: ["420D nylon body.", "Soft-touch face fabric.", "YKK zippers."] },
        { id: "documentation", title: "Documentation",
          links: [{ label: "EDC 2-Day - Spec Sheet", href: "#" }] },
        { id: "partNumbers", title: "Part Numbers",
          parts: [
            { sku: "EDC2D-BLACK",  color: "Black",  size: null },
            { sku: "EDC2D-SHADOW", color: "Shadow", size: null },
            { sku: "EDC2D-SAND",   color: "Sand",   size: null },
          ] },
      ],
    },
  },
  {
    id: "scree-32",
    sku: "SCREE32",
    name: "Scree 32 Mock",
    tagline: "Light, fast, simple.",
    shortDescription:
      "A 32 L day pack stripped to essentials. Hydration sleeve, clean webbing, no extra weight.",
    description:
      "When the goal is to move and the gear list is short, Scree 32 carries it without fuss. A clean silhouette, simple top-loader, hydration-ready, and a hip belt that disappears when you don't need it.",
    price: { amount: 199, currency: "USD" },
    prices: [{ amount: 199, currency: "USD" }],
    priceLevels: { base: 199, msrp: 219, wholesale: 159 },
    image: "/rats-coyote.avif",
    gallery: ["/rats-coyote.avif"],
    colors: [
      { id: "black", name: "Black", hex: "#1a1a1a", image: "/rats-black.avif", priceAdjustment: 0 },
      { id: "sand", name: "Sand", hex: "#cdb892", image: "/rats-coyote.avif", priceAdjustment: 0 },
    ],
    sizes: [],
    volume: 32,
    productLine: "everyday",
    intendedUse: ["Daypack"],
    frameSystem: "Frameless",
    isNew: false,
    rating: 4.4,
    reviewCount: 57,
    specs: [
      { label: "Weight",      us: "1 lb 12 oz",  metric: "0.79 kg" },
      { label: "Volume",      us: "1955 cu-ins", metric: "32 L",
            hint: "How is volume calculated?" },
      { label: "Dimensions",  us: '21"x11"x8"',  metric: "53x28x20 cm" },
      { label: "Frame System", value: "Frameless" },
    ],
    origin: { label: "Made in Vietnam", note: "Lifetime warranty", flag: "🌏" },
    attributes: {
      defaultOpen: "keyAttributes",
      sections: [
        { id: "keyAttributes", title: "Key Attributes",
          bullets: [
            "Lightweight build for trail days and travel.",
            "Hydration sleeve fits a 3 L reservoir.",
            "Removable hip belt for quick mode-switching.",
          ] },
        { id: "features", title: "Features",
          bullets: [
            "Top-loading main with floating lid.",
            "Stretch side pockets.",
            "Sternum strap with whistle buckle.",
          ] },
        { id: "materials", title: "Materials",
          bullets: ["330D Cordura body.", "DWR coating.", "YKK zippers."] },
        { id: "documentation", title: "Documentation",
          links: [{ label: "Scree 32 - Spec Sheet", href: "#" }] },
        { id: "partNumbers", title: "Part Numbers",
          parts: [
            { sku: "SCREE32-BLACK", color: "Black", size: null },
            { sku: "SCREE32-SAND",  color: "Sand",  size: null },
          ] },
      ],
    },
  },
  {
    id: "lid-pouch",
    sku: "LIDP",
    name: "Lid Pouch Mock",
    tagline: "A little more room, right where you need it.",
    shortDescription:
      "Clip-on lid pouch for any NICE-frame pack. Key clip, zip security pocket, fast-grab top.",
    description:
      "Add-on storage that earns its place. The Lid Pouch attaches to any NICE-mount pack lid for quick-grab essentials — keys, headlamp, snacks — and a separate zip security pocket for things you don't want loose in the main bag.",
    price: { amount: 65, currency: "USD" },
    prices: [{ amount: 65, currency: "USD" }],
    priceLevels: { base: 65, msrp: 75, wholesale: 52 },
    image: "/rats-black.avif",
    gallery: ["/rats-black.avif"],
    colors: [
      { id: "black", name: "Black", hex: "#1a1a1a", image: "/rats-black.avif", priceAdjustment: 0 },
      { id: "coyote", name: "Coyote", hex: "#a78a6b", image: "/rats-coyote.avif", priceAdjustment: 0 },
    ],
    sizes: [],
    volume: 4,
    productLine: "accessories",
    intendedUse: ["Add-on"],
    frameSystem: "",
    isNew: false,
    rating: 4.2,
    reviewCount: 19,
    specs: [
      { label: "Weight",     us: "5 oz",       metric: "142 g" },
      { label: "Volume",     us: "245 cu-ins", metric: "4 L" },
      { label: "Compatibility", value: "NICE-mount packs (RATS, Blackjack 100)" },
    ],
    origin: { label: "Made in Vietnam", note: "Lifetime warranty", flag: "🌏" },
    attributes: {
      defaultOpen: "keyAttributes",
      sections: [
        { id: "keyAttributes", title: "Key Attributes",
          bullets: [
            "Clips to any NICE-mount pack lid.",
            "Two zip pockets — quick-grab and security.",
            "Internal key clip.",
          ] },
        { id: "features", title: "Features",
          bullets: [
            "Reflective trim for low-light visibility.",
            "Webbing pull tabs sized for gloves.",
          ] },
        { id: "materials", title: "Materials",
          bullets: ["500D Cordura body.", "YKK zippers."] },
        { id: "documentation", title: "Documentation",
          links: [{ label: "Lid Pouch - Spec Sheet", href: "#" }] },
        { id: "partNumbers", title: "Part Numbers",
          parts: [
            { sku: "LIDP-BLACK",  color: "Black",  size: null },
            { sku: "LIDP-COYOTE", color: "Coyote", size: null },
          ] },
      ],
    },
  },
];

export function findMockItem(idOrSku) {
  const k = String(idOrSku);
  return MOCK_ITEMS.find((i) => String(i.id) === k || i.sku === k) || null;
}

export function listMockItems({ productLine } = {}) {
  let items = MOCK_ITEMS;
  if (productLine) items = items.filter((i) => i.productLine === productLine);
  return { items };
}
